import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { styles } from './asc-select.styles';

export type SelectType =
  | 'default'
  | 'icon-leading'
  | 'avatar-leading'
  | 'dot-leading'
  | 'search'
  | 'tags';

export type SelectSize = 'sm' | 'md';

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

const CHEVRON_DOWN_SVG = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="12"
  height="12"
  viewBox="0 0 12 12"
  fill="none"
  aria-hidden="true"
  focusable="false"
>
  <path
    d="M2.5 4.5L6 8L9.5 4.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

const CHECK_SVG = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  aria-hidden="true"
  focusable="false"
>
  <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5" />
  <path
    d="M5 8L7 10.5L11 5.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

const QUESTION_SVG = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  aria-hidden="true"
  focusable="false"
>
  <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5" />
  <path
    d="M6.5 6.5C6.5 5.67 7.17 5 8 5C8.83 5 9.5 5.67 9.5 6.5C9.5 7.5 8 7.8 8 9"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
  />
  <circle cx="8" cy="11" r="0.75" fill="currentColor" />
</svg>`;

const SEARCH_SVG = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 20 20"
  fill="none"
  aria-hidden="true"
  focusable="false"
>
  <g clip-path="url(#asc-select-search-clip)">
    <path
      d="M19.3703 19.9968C19.2036 19.9968 19.0461 19.9318 18.9286 19.8135L13.4995 14.3835C12.8653 14.926 12.1511 15.3685 11.372 15.6993C10.3478 16.1343 9.27196 16.3552 8.17446 16.3552C7.12696 16.3552 6.09779 16.1543 5.11362 15.7568C3.08779 14.9393 1.50196 13.3827 0.64779 11.3718C-0.206376 9.36182 -0.22721 7.13849 0.59029 5.11349C1.40779 3.08849 2.96446 1.50182 4.97446 0.647655C5.99862 0.211821 7.07362 -0.00817871 8.17196 -0.00817871C9.21946 -0.00817871 10.2486 0.193488 11.2328 0.590155C13.2586 1.40765 14.8445 2.96432 15.6986 4.97515C16.5528 6.98599 16.5736 9.20849 15.7561 11.2335C15.4211 12.0635 14.9595 12.8243 14.382 13.4985L19.8128 18.9302C20.0561 19.1735 20.0561 19.5702 19.8128 19.8143C19.6945 19.9318 19.537 19.9968 19.3703 19.9968ZM8.17196 1.24182C7.24279 1.24182 6.33112 1.42849 5.46362 1.79765C3.76029 2.52182 2.44112 3.86515 1.74862 5.58099C1.05612 7.29682 1.07362 9.17932 1.79696 10.8827C2.52112 12.586 3.86446 13.9052 5.58029 14.5977C6.41446 14.9343 7.28696 15.1043 8.17362 15.1043C9.10279 15.1043 10.0145 14.9177 10.882 14.5485C11.6936 14.2035 12.4211 13.7168 13.0436 13.1018C13.052 13.0902 13.0611 13.0802 13.0703 13.071C13.0836 13.0577 13.0961 13.0468 13.1078 13.0385C13.7436 12.396 14.2486 11.6277 14.5961 10.7652C15.2886 9.04932 15.2711 7.16682 14.547 5.46349C13.8236 3.76016 12.4795 2.44099 10.7636 1.74849C9.93112 1.41265 9.05862 1.24182 8.17196 1.24182Z"
      fill="currentColor"
    />
  </g>
  <defs>
    <clipPath id="asc-select-search-clip">
      <rect width="20" height="20" fill="white" />
    </clipPath>
  </defs>
</svg>`;

const CLOSE_SVG = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="9"
  height="9"
  viewBox="0 0 9 9"
  fill="none"
  aria-hidden="true"
  focusable="false"
>
  <mask id="path-1-inside-1_20971_301" fill="white">
    <path d="M7.875 0C7.975 0 8.06915 0.0393516 8.13965 0.110352C8.21065 0.180852 8.25 0.275 8.25 0.375C8.25 0.475 8.21065 0.569148 8.13965 0.639648L4.65527 4.125L8.14062 7.61035C8.28639 7.75637 8.28655 7.9942 8.14062 8.14062C8.06962 8.21113 7.975 8.25 7.875 8.25C7.775 8.25 7.68085 8.21065 7.61035 8.13965L4.125 4.65527L0.639648 8.14062C0.568676 8.21147 0.474888 8.25098 0.375 8.25098C0.275112 8.25098 0.180826 8.21147 0.110352 8.14062C-0.0356482 7.99463 -0.0356477 7.75685 0.110352 7.61035L3.59473 4.125L0.110352 0.639648C0.0393516 0.569148 0 0.475 0 0.375C0 0.275 0.0393516 0.180852 0.110352 0.110352C0.180852 0.0393516 0.275 0 0.375 0C0.475 0 0.569148 0.0393516 0.639648 0.110352L4.125 3.59473L7.61035 0.110352C7.68135 0.0393517 7.775 0 7.875 0Z"/>
  </mask>
  <path d="M7.875 0C7.975 0 8.06915 0.0393516 8.13965 0.110352C8.21065 0.180852 8.25 0.275 8.25 0.375C8.25 0.475 8.21065 0.569148 8.13965 0.639648L4.65527 4.125L8.14062 7.61035C8.28639 7.75637 8.28655 7.9942 8.14062 8.14062C8.06962 8.21113 7.975 8.25 7.875 8.25C7.775 8.25 7.68085 8.21065 7.61035 8.13965L4.125 4.65527L0.639648 8.14062C0.568676 8.21147 0.474888 8.25098 0.375 8.25098C0.275112 8.25098 0.180826 8.21147 0.110352 8.14062C-0.0356482 7.99463 -0.0356477 7.75685 0.110352 7.61035L3.59473 4.125L0.110352 0.639648C0.0393516 0.569148 0 0.475 0 0.375C0 0.275 0.0393516 0.180852 0.110352 0.110352C0.180852 0.0393516 0.275 0 0.375 0C0.475 0 0.569148 0.0393516 0.639648 0.110352L4.125 3.59473L7.61035 0.110352C7.68135 0.0393517 7.775 0 7.875 0Z" fill="currentColor"/>
  <path d="M8.13965 0.110352L7.07522 1.16728L7.08274 1.17475L8.13965 0.110352ZM8.13965 0.639648L7.08274 -0.42476L7.07884 -0.420863L8.13965 0.639648ZM4.65527 4.125L3.59446 3.06449L2.5341 4.12515L3.59461 5.18566L4.65527 4.125ZM8.14062 7.61035L9.20223 6.55063L9.20129 6.54969L8.14062 7.61035ZM8.14062 8.14062L9.19755 9.20504L9.2031 9.19947L8.14062 8.14062ZM7.61035 8.13965L8.67476 7.08274L8.67086 7.07884L7.61035 8.13965ZM4.125 4.65527L5.18551 3.59446L4.12485 2.5341L3.06434 3.59461L4.125 4.65527ZM0.639648 8.14062L1.69939 9.2022L1.70031 9.20129L0.639648 8.14062ZM0.110352 8.14062L1.17382 7.08277L1.17101 7.07996L0.110352 8.14062ZM0.110352 7.61035L-0.950459 6.54984L-0.952123 6.55151L0.110352 7.61035ZM3.59473 4.125L4.65554 5.18551L5.71575 4.125L4.65554 3.06449L3.59473 4.125ZM0.110352 0.639648L1.17117 -0.42087L1.16726 -0.424753L0.110352 0.639648ZM0.110352 0.110352L1.16728 1.17478L1.17475 1.16726L0.110352 0.110352ZM0.639648 0.110352L-0.42476 1.16726L-0.420863 1.17116L0.639648 0.110352ZM4.125 3.59473L3.06449 4.65554L4.125 5.71575L5.18551 4.65554L4.125 3.59473ZM7.61035 0.110352L8.67086 1.17116L8.67101 1.17101L7.61035 0.110352ZM7.875 0V1.5C7.57358 1.5 7.28511 1.37861 7.07525 1.16726L8.13965 0.110352L9.20405 -0.946554C8.85319 -1.29991 8.37642 -1.5 7.875 -1.5V0ZM8.13965 0.110352L7.08274 1.17475C6.87139 0.964888 6.75 0.676416 6.75 0.375H8.25H9.75C9.75 -0.126416 9.54991 -0.603185 9.19655 -0.95405L8.13965 0.110352ZM8.25 0.375H6.75C6.75 0.0735838 6.87139 -0.214888 7.08274 -0.424753L8.13965 0.639648L9.19655 1.70405C9.54991 1.35319 9.75 0.876416 9.75 0.375H8.25ZM8.13965 0.639648L7.07884 -0.420863L3.59446 3.06449L4.65527 4.125L5.71608 5.18551L9.20046 1.70016L8.13965 0.639648ZM4.65527 4.125L3.59461 5.18566L7.07996 8.67101L8.14062 7.61035L9.20129 6.54969L5.71593 3.06434L4.65527 4.125ZM8.14062 7.61035L7.07902 8.67007C6.64019 8.23045 6.64077 7.52066 7.07815 7.08178L8.14062 8.14062L9.2031 9.19947C9.93232 8.46775 9.93259 7.28229 9.20223 6.55063L8.14062 7.61035ZM8.14062 8.14062L7.08372 7.07622C7.29453 6.86689 7.58017 6.75 7.875 6.75V8.25V9.75C8.36983 9.75 8.84472 9.55536 9.19753 9.20503L8.14062 8.14062ZM7.875 8.25V6.75C8.17642 6.75 8.46489 6.87139 8.67475 7.08274L7.61035 8.13965L6.54595 9.19655C6.89681 9.54991 7.37358 9.75 7.875 9.75V8.25ZM7.61035 8.13965L8.67086 7.07884L5.18551 3.59446L4.125 4.65527L3.06449 5.71608L6.54984 9.20046L7.61035 8.13965ZM4.125 4.65527L3.06434 3.59461L-0.421012 7.07996L0.639648 8.14062L1.70031 9.20129L5.18566 5.71593L4.125 4.65527ZM0.639648 8.14062L-0.420092 7.07905C-0.215568 6.87488 0.0694846 6.75098 0.375 6.75098V8.25098V9.75098C0.880292 9.75098 1.35292 9.54807 1.69939 9.2022L0.639648 8.14062ZM0.375 8.25098V6.75098C0.677506 6.75098 0.965021 6.87287 1.17382 7.08278L0.110352 8.14062L-0.953112 9.19847C-0.603368 9.55007 -0.127282 9.75098 0.375 9.75098V8.25098ZM0.110352 8.14062L1.17101 7.07996C1.6118 7.52075 1.60919 8.23133 1.17283 8.66919L0.110352 7.61035L-0.952123 6.55151C-1.68049 7.28237 -1.6831 8.4685 -0.950309 9.20129L0.110352 8.14062ZM0.110352 7.61035L1.17116 8.67086L4.65554 5.18551L3.59473 4.125L2.53392 3.06449L-0.950457 6.54984L0.110352 7.61035ZM3.59473 4.125L4.65554 3.06449L1.17116 -0.420863L0.110352 0.639648L-0.950457 1.70016L2.53392 5.18551L3.59473 4.125ZM0.110352 0.639648L1.16726 -0.424753C1.37861 -0.214888 1.5 0.0735838 1.5 0.375H0H-1.5C-1.5 0.876416 -1.29991 1.35319 -0.946554 1.70405L0.110352 0.639648ZM0 0.375H1.5C1.5 0.676416 1.37861 0.964888 1.16726 1.17475L0.110352 0.110352L-0.946554 -0.95405C-1.29991 -0.603185 -1.5 -0.126416 -1.5 0.375H0ZM0.110352 0.110352L1.17475 1.16726C0.964888 1.37861 0.676416 1.5 0.375 1.5V0V-1.5C-0.126416 -1.5 -0.603185 -1.29991 -0.95405 -0.946554L0.110352 0.110352ZM0.375 0V1.5C0.0735838 1.5 -0.214888 1.37861 -0.424753 1.16726L0.639648 0.110352L1.70405 -0.946554C1.35319 -1.29991 0.876416 -1.5 0.375 -1.5V0ZM0.639648 0.110352L-0.420863 1.17116L3.06449 4.65554L4.125 3.59473L5.18551 2.53392L1.70016 -0.950457L0.639648 0.110352ZM4.125 3.59473L5.18551 4.65554L8.67086 1.17116L7.61035 0.110352L6.54984 -0.950457L3.06449 2.53392L4.125 3.59473ZM7.61035 0.110352L8.67101 1.17101C8.46543 1.37659 8.17945 1.5 7.875 1.5V0V-1.5C7.37055 -1.5 6.89727 -1.29789 6.54969 -0.950309L7.61035 0.110352Z" fill="currentColor" mask="url(#path-1-inside-1_20971_301)"/>
</svg>`;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A self-contained select form control composed of a styled trigger and a
 * dropdown listbox anchored directly below. Options are arbitrary rich-content
 * elements slotted by the consumer — no companion element required. Each
 * slotted option must carry a `data-value` attribute (unique identifier) and
 * a `data-label` attribute (plain-text label for trigger display and filtering).
 *
 * The full slotted element is cloned into the dropdown `<li>` wrapper — its
 * internal structure is preserved. The original stays in light DOM (hidden via
 * CSS) to keep all option content — headings, avatar alt text, rich text —
 * crawlable by search engines.
 *
 * Comes in two sizes (`sm`, `md`) and six display types:
 * `default`, `icon-leading`, `avatar-leading`, `dot-leading`, `search`, `tags`.
 *
 * @element asc-select
 *
 * @fires asc-change - Fired on selection change.
 *   Single types: `{ value: string }`.
 *   Tags type: `{ values: string[] }`.
 * @fires asc-open  - Fired when the dropdown panel opens. No detail.
 * @fires asc-close - Fired when the dropdown panel closes. No detail.
 *
 * @slot           - Option elements. Each must carry `data-value` and `data-label` attributes.
 *                   Accepts rich content — `<span>`, `<div>`, avatar components, etc.
 *                   Originals stay in light DOM for SEO; clones render in the Shadow DOM listbox.
 * @slot label     - Field label rendered above the trigger. Row is hidden when omitted.
 * @slot hint-text - Helper text rendered below the trigger. Hidden when omitted.
 * @slot prefix-icon - Leading icon, avatar, or dot rendered inside the trigger to the left of
 *                     the selected value. Hidden when nothing is slotted.
 *
 * @csspart base               - Root wrapper `<div>`.
 * @csspart label-row          - Row containing label, required indicator, and help icon.
 * @csspart label              - `<span>` wrapping the label slot.
 * @csspart required-indicator - The `*` asterisk next to the label.
 * @csspart help-icon          - Question-circle icon `<span>`.
 * @csspart trigger            - Clickable trigger (`<button>` for default/icon/avatar/dot types;
 *                               `<div>` wrapper for search/tags types).
 * @csspart search-icon        - Search icon `<span>` (search and tags types).
 * @csspart prefix             - Leading icon/avatar slot container.
 * @csspart display-value      - Selected `data-label` text `<span>`.
 * @csspart placeholder-text   - Placeholder text `<span>`.
 * @csspart search-input       - Editable `<input>` (search and tags types).
 * @csspart chevron            - Dropdown arrow icon `<span>`.
 * @csspart tags-input-area    - Flex-wrap row holding tag chips and the text input (tags type).
 * @csspart tag                - Individual selected-value chip `<span>`.
 * @csspart tag-content        - Inner flex row inside a chip (avatar + label).
 * @csspart tag-avatar         - Avatar `<img>` inside a chip (present when option has `data-avatar`).
 * @csspart tag-remove         - Dismiss `<button>` on each chip.
 * @csspart panel              - The dropdown container `<div>`.
 * @csspart menu               - The `<ul role="listbox">` element.
 * @csspart menu-item          - Option row `<li role="option">`. Also carries `menu-item--selected`.
 * @csspart menu-item-check    - Check-circle icon shown on the selected option.
 * @csspart menu-empty         - Empty-state message shown when no options match the typed filter.
 * @csspart hint-text          - Hint text container `<div>`.
 *
 * @cssprop --asc-select-trigger-bg                    - Trigger background (idle).
 * @cssprop --asc-select-trigger-bg-disabled           - Trigger background (disabled).
 * @cssprop --asc-select-trigger-border-color          - Trigger border color (idle).
 * @cssprop --asc-select-trigger-border-color-focus    - Trigger border color (focused/open).
 * @cssprop --asc-select-trigger-border-color-disabled - Trigger border color (disabled).
 * @cssprop --asc-select-trigger-radius                - Trigger border radius.
 * @cssprop --asc-select-trigger-shadow                - Trigger box shadow.
 * @cssprop --asc-select-trigger-padding-x             - Trigger horizontal padding.
 * @cssprop --asc-select-trigger-padding-y             - Trigger vertical padding.
 * @cssprop --asc-select-placeholder-color             - Placeholder text color.
 * @cssprop --asc-select-value-color                   - Selected label text color.
 * @cssprop --asc-select-value-font-weight             - Selected label font weight.
 * @cssprop --asc-select-search-color                  - Search/filter input text color.
 * @cssprop --asc-select-text-disabled                 - Text color (disabled).
 * @cssprop --asc-select-label-color                   - Label text color.
 * @cssprop --asc-select-label-font-size               - Label font size.
 * @cssprop --asc-select-label-font-weight             - Label font weight.
 * @cssprop --asc-select-required-color                - Required asterisk color.
 * @cssprop --asc-select-hint-color                    - Hint text color.
 * @cssprop --asc-select-hint-font-size                - Hint font size.
 * @cssprop --asc-select-menu-bg                       - Dropdown panel background.
 * @cssprop --asc-select-menu-border-color             - Dropdown panel border color.
 * @cssprop --asc-select-menu-radius                   - Dropdown panel border radius.
 * @cssprop --asc-select-menu-shadow                   - Dropdown panel box shadow.
 * @cssprop --asc-select-menu-max-height               - Dropdown max height before scrolling.
 * @cssprop --asc-select-item-padding                  - Option row inner padding.
 * @cssprop --asc-select-item-radius                   - Option row border radius.
 * @cssprop --asc-select-item-bg-selected              - Background of the selected option.
 * @cssprop --asc-select-item-bg-hover                 - Background on hover/focus.
 * @cssprop --asc-select-tag-bg                        - Tag chip background.
 * @cssprop --asc-select-tag-color                     - Tag chip text color.
 * @cssprop --asc-select-tag-radius                    - Tag chip border radius.
 * @cssprop --asc-select-chevron-size                  - Chevron icon size.
 * @cssprop --asc-select-empty-color                   - Empty-state "No results" text color. Default: `var(--asc-text-tertiary)`
 */
@customElement('asc-select')
export class AscSelect extends LitElement {
  static override styles = styles;

  private readonly _uid = generateUid();

  // ─── Reflected properties ──────────────────────────────────────────────────

  /** Determines the trigger layout and interaction mode. */
  @property({ type: String, reflect: true }) type: SelectType = 'default';

  /** Controls trigger padding and typography scale. */
  @property({ type: String, reflect: true }) size: SelectSize = 'sm';

  /** Muted text shown in the trigger when no value is selected. */
  @property({ type: String }) placeholder = 'Select option';

  /** The `data-value` of the currently selected option (single-select types). */
  @property({ type: String, reflect: true }) value = '';

  /** Array of selected `data-value` strings (tags type only). Not reflected to attribute. */
  @property({ type: Array }) values: string[] = [];

  /** Whether the dropdown panel is currently visible. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Disables all interaction and applies disabled visual treatment. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Renders a brand-colored `*` asterisk immediately after the label text. */
  @property({ type: Boolean, reflect: true }) required = false;

  /** Renders a question-circle icon at the end of the label row. */
  @property({ type: Boolean, reflect: true, attribute: 'help-icon' }) helpIcon = false;

  // ─── Internal state ────────────────────────────────────────────────────────

  @state() private _options: Element[] = [];
  @state() private _optionClones: Node[] = [];
  @state() private _focusedIndex = -1;
  @state() private _hiddenValues = new Set<string>();
  @state() private _hasLabel = false;
  @state() private _hasHintText = false;
  @state() private _hasPrefixIcon = false;

  /** The current user-typed filter string (search and tags types). Read-only. */
  @state() private _searchValue = '';
  get searchValue(): string {
    return this._searchValue;
  }

  // ─── DOM queries ───────────────────────────────────────────────────────────

  @query('[part="search-input"]') private _searchInputEl!: HTMLInputElement;

  // ─── Document mousedown handler (arrow fn preserves `this`) ───────────────

  private readonly _handleDocumentMousedown = (e: MouseEvent): void => {
    if (!e.composedPath().includes(this)) {
      this._closePanel(false);
    }
  };

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleDocumentMousedown);
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);

    // Sync aria-disabled on the host element
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');

    if (changed.has('open')) {
      if (this.open) {
        document.addEventListener('mousedown', this._handleDocumentMousedown);
        // Move focus to first visible option after Lit renders the panel for
        // non-editable variants. Search/tags keep focus on the input so users
        // can continue typing/filtering after the panel opens.
        if (this.type !== 'search' && this.type !== 'tags') {
          void this.updateComplete.then(() => {
            const firstIdx = this._firstVisibleIndex();
            if (firstIdx >= 0) {
              this._focusItem(firstIdx);
            }
          });
        }
      } else {
        document.removeEventListener('mousedown', this._handleDocumentMousedown);
        this._focusedIndex = -1;
      }
    }
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private _isSearchType(): boolean {
    return this.type === 'search' || this.type === 'tags';
  }

  private _firstVisibleIndex(): number {
    return this._options.findIndex(
      (opt) => !this._hiddenValues.has(opt.getAttribute('data-value') ?? '')
    );
  }

  private _nextVisibleIndex(from: number, delta: 1 | -1): number {
    const count = this._options.length;
    if (count === 0) return -1;
    let next = (from + delta + count) % count;
    let attempts = 0;
    while (
      this._hiddenValues.has(this._options[next]?.getAttribute('data-value') ?? '') &&
      attempts < count
    ) {
      next = (next + delta + count) % count;
      attempts++;
    }
    return this._hiddenValues.has(this._options[next]?.getAttribute('data-value') ?? '')
      ? -1
      : next;
  }

  private _focusItem(index: number): void {
    if (index < 0) return;
    this._focusedIndex = index;
    void this.updateComplete.then(() => {
      const el = this.shadowRoot?.querySelector<HTMLElement>(
        `[id="${this._uid}-option-${index}"]`
      );
      el?.focus();
    });
  }

  private _focusTrigger(): void {
    const sel = this._isSearchType() ? '[part="search-input"]' : '[part="trigger"]';
    const el = this.shadowRoot?.querySelector<HTMLElement>(sel);
    el?.focus();
  }

  private _hasNoResults(): boolean {
    return (
      this._searchValue.trim() !== '' &&
      this._options.length > 0 &&
      this._options.every((opt) =>
        this._hiddenValues.has(opt.getAttribute('data-value') ?? '')
      )
    );
  }

  private _getSelectedLabel(): string {
    return (
      this._options
        .find((el) => el.getAttribute('data-value') === this.value)
        ?.getAttribute('data-label') ?? ''
    );
  }

  private _openPanel(): void {
    if (this.disabled || this.open) return;
    this.open = true;
    dispatch(this, 'asc-open');
  }

  private _closePanel(returnFocus = true): void {
    if (!this.open) return;
    this.open = false;
    dispatch(this, 'asc-close');
    if (returnFocus) this._focusTrigger();
  }

  private _selectOption(index: number): void {
    const opt = this._options[index];
    if (!opt) return;
    const val = opt.getAttribute('data-value') ?? '';

    if (this.type === 'tags') {
      const next = this.values.includes(val)
        ? this.values.filter((v) => v !== val)
        : [...this.values, val];
      this.values = next;
      dispatch<{ values: string[] }>(this, 'asc-change', { detail: { values: this.values } });
      // Tags panel stays open
    } else {
      this.value = val;
      // For search type: sync the input text to the selected label and clear the filter.
      if (this._isSearchType()) {
        this._searchValue =
          opt.getAttribute('data-label') ?? val;
        this._hiddenValues = new Set();
      }
      dispatch<{ value: string }>(this, 'asc-change', { detail: { value: val } });
      this._closePanel(true);
    }
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  private _handleSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._options = slot.assignedElements();
    // Pre-compute clones once; stable references prevent unnecessary DOM churn on re-renders
    this._optionClones = this._options.map((opt) => opt.cloneNode(true));
  }

  private _handleLabelSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasLabel = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleHintSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasHintText = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handlePrefixSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasPrefixIcon = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleTriggerClick(): void {
    if (this.disabled) return;
    if (this.open) {
      this._closePanel(false);
    } else {
      this._openPanel();
    }
  }

  private _handleTriggerKeyDown(e: KeyboardEvent): void {
    if (this.disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!this.open) this._openPanel();
    } else if (e.key === 'Escape') {
      this._closePanel(true);
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.open) {
      e.preventDefault();
      this._openPanel();
    }
  }

  private _handleMenuKeyDown(e: KeyboardEvent): void {
    const count = this._options.length;
    if (count === 0) return;

    const current = this._focusedIndex >= 0 ? this._focusedIndex : this._firstVisibleIndex();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._focusItem(this._nextVisibleIndex(current, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._focusItem(this._nextVisibleIndex(current, -1));
        break;
      case 'Home':
        e.preventDefault();
        this._focusItem(this._firstVisibleIndex());
        break;
      case 'End': {
        e.preventDefault();
        // Find last visible option index
        let lastIdx = count - 1;
        while (
          lastIdx >= 0 &&
          this._hiddenValues.has(this._options[lastIdx]?.getAttribute('data-value') ?? '')
        ) {
          lastIdx--;
        }
        if (lastIdx >= 0) this._focusItem(lastIdx);
        break;
      }
      case 'Enter':
        e.preventDefault();
        if (this._focusedIndex >= 0) this._selectOption(this._focusedIndex);
        break;
      case ' ':
        if (this.type === 'tags') {
          e.preventDefault();
          if (this._focusedIndex >= 0) this._selectOption(this._focusedIndex);
        }
        break;
      case 'Escape':
        this._closePanel(true);
        break;
      case 'Tab':
        this._closePanel(false);
        break;
    }
  }

  private _handleOptionClick(index: number): void {
    this._selectOption(index);
  }

  private _handleTagRemove(e: Event, val: string): void {
    e.stopPropagation();
    const next = this.values.filter((v) => v !== val);
    this.values = next;
    dispatch<{ values: string[] }>(this, 'asc-change', { detail: { values: this.values } });
  }

  private _handleSearchInput(e: InputEvent): void {
    const input = e.target as HTMLInputElement;
    this._searchValue = input.value;
    const q = this._searchValue.toLowerCase();
    const hidden = new Set<string>();
    for (const opt of this._options) {
      const label = (opt.getAttribute('data-label') ?? '').toLowerCase();
      if (q && !label.includes(q)) {
        const val = opt.getAttribute('data-value') ?? '';
        hidden.add(val);
      }
    }
    this._hiddenValues = hidden;
    if (!this.open) this._openPanel();
  }

  private _handleSearchKeyDown(e: KeyboardEvent): void {
    // Backspace on empty input removes the last tag chip
    if (
      e.key === 'Backspace' &&
      this._searchValue === '' &&
      this.type === 'tags' &&
      this.values.length > 0
    ) {
      this.values = this.values.slice(0, -1);
      dispatch<{ values: string[] }>(this, 'asc-change', { detail: { values: this.values } });
      return;
    }
    // Arrow keys open panel and move focus into menu
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.open) {
      e.preventDefault();
      this._openPanel();
      return;
    }
    if (e.key === 'Escape') {
      this._closePanel(true);
      return;
    }
    // When panel is open, delegate Arrow/Home/End/Enter/Space/Tab to menu handler
    if (
      this.open &&
      (e.key === 'ArrowDown' ||
        e.key === 'ArrowUp' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === 'Enter' ||
        e.key === 'Tab')
    ) {
      this._handleMenuKeyDown(e);
    }
  }

  // ─── Render helpers ────────────────────────────────────────────────────────

  private _renderButtonTrigger(listboxId: string, labelId: string, hasLabelRow: boolean) {
    const activeDescendant =
      this._focusedIndex >= 0 ? `${this._uid}-option-${this._focusedIndex}` : undefined;
    const selectedLabel = this._getSelectedLabel();

    return html`
      <button
        part="trigger"
        type="button"
        role="combobox"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-haspopup="listbox"
        aria-controls=${listboxId}
        aria-labelledby=${hasLabelRow ? labelId : nothing}
        aria-label=${!hasLabelRow ? (this.placeholder || 'Select') : nothing}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-activedescendant=${ifDefined(activeDescendant)}
        ?disabled=${this.disabled}
        @click=${this._handleTriggerClick}
        @keydown=${this._handleTriggerKeyDown}
      >
        <span part="prefix" ?hidden=${!this._hasPrefixIcon}><slot name="prefix-icon" @slotchange=${this._handlePrefixSlotChange}></slot></span>
        ${this.value
          ? html`<span part="display-value">${selectedLabel}</span>`
          : html`<span part="placeholder-text">${this.placeholder}</span>`}
        <span part="chevron" aria-hidden="true">${CHEVRON_DOWN_SVG}</span>
      </button>
    `;
  }

  private _renderSearchTrigger(listboxId: string, labelId: string, hasLabelRow: boolean) {
    const activeDescendant =
      this._focusedIndex >= 0 ? `${this._uid}-option-${this._focusedIndex}` : undefined;

    return html`
      <div
        part="trigger"
        @click=${() => this._searchInputEl?.focus()}
      >
        <span part="search-icon" aria-hidden="true">${SEARCH_SVG}</span>
        <div part="tags-input-area">
          ${this.type === 'tags'
            ? this.values.map((val) => {
                const opt = this._options.find((o) => o.getAttribute('data-value') === val);
                const label = opt?.getAttribute('data-label') ?? val;
                const avatarUrl = opt?.getAttribute('data-avatar') ?? '';
                return html`
                  <span part="tag">
                    <span part="tag-content">
                      ${avatarUrl
                        ? html`<img part="tag-avatar" src=${avatarUrl} alt="" />`
                        : nothing}
                      <span>${label}</span>
                    </span>
                    <button
                      part="tag-remove"
                      type="button"
                      aria-label="Remove ${label}"
                      @click=${(e: Event) => this._handleTagRemove(e, val)}
                    >
                      ${CLOSE_SVG}
                    </button>
                  </span>
                `;
              })
            : nothing}
          <input
            part="search-input"
            type="text"
            role="combobox"
            aria-expanded=${this.open ? 'true' : 'false'}
            aria-haspopup="listbox"
            aria-controls=${listboxId}
            aria-labelledby=${hasLabelRow ? labelId : nothing}
            aria-label=${!hasLabelRow ? (this.placeholder || 'Select') : nothing}
            aria-disabled=${this.disabled ? 'true' : 'false'}
            aria-autocomplete="list"
            aria-activedescendant=${ifDefined(activeDescendant)}
            ?disabled=${this.disabled}
            .value=${live(this._searchValue)}
            placeholder=${this.placeholder}
            @input=${this._handleSearchInput}
            @keydown=${this._handleSearchKeyDown}
            @focus=${() => {
              if (!this.open) this._openPanel();
            }}
          />
        </div>
      </div>
    `;
  }

  // ─── render() ─────────────────────────────────────────────────────────────

  override render() {
    const listboxId = `${this._uid}-listbox`;
    const labelId = `${this._uid}-label`;
    const showLabelRow = this._hasLabel || this.required || this.helpIcon;

    return html`
      <div part="base">
        <!-- Label row: hidden when no label content, required, or helpIcon -->
        <div part="label-row" id=${labelId} ?hidden=${!showLabelRow}>
          <span part="label">
            <slot name="label" @slotchange=${this._handleLabelSlotChange}></slot>
          </span>
          ${this.required
            ? html`<span part="required-indicator" aria-hidden="true">*</span>`
            : nothing}
          ${this.helpIcon
            ? html`<span part="help-icon" aria-hidden="true">${QUESTION_SVG}</span>`
            : nothing}
        </div>

        <!-- Trigger + panel share a positioning context so the dropdown
             is anchored immediately below the trigger, not the whole host. -->
        <div part="trigger-wrapper">

        <!-- Trigger (button or div+input depending on type) -->
        ${this._isSearchType()
          ? this._renderSearchTrigger(listboxId, labelId, showLabelRow)
          : this._renderButtonTrigger(listboxId, labelId, showLabelRow)}

        <!-- Dropdown panel -->
        <div part="panel">
          <ul
            part="menu"
            id=${listboxId}
            role="listbox"
            aria-multiselectable=${this.type === 'tags' ? 'true' : 'false'}
            aria-labelledby=${this._hasLabel ? labelId : nothing}
            aria-label=${!this._hasLabel ? (this.placeholder || 'Select') : nothing}
            @keydown=${this._handleMenuKeyDown}
          >
            ${this._options.map((opt, i) => {
              const val = opt.getAttribute('data-value') ?? '';
              const isSelected =
                this.type === 'tags' ? this.values.includes(val) : this.value === val;
              const isHidden = this._hiddenValues.has(val);

              return html`
                <li
                  part="menu-item${isSelected ? ' menu-item--selected' : ''}"
                  role="option"
                  id="${this._uid}-option-${i}"
                  aria-selected=${isSelected ? 'true' : 'false'}
                  tabindex="-1"
                  ?hidden=${isHidden}
                  @click=${() => this._handleOptionClick(i)}
                >
                  ${this._optionClones[i]}
                  ${isSelected
                    ? html`<span part="menu-item-check" aria-hidden="true">${CHECK_SVG}</span>`
                    : nothing}
                </li>
              `;
            })}
          </ul>
          ${this._hasNoResults()
            ? html`<div part="menu-empty" role="status" aria-live="polite">No results</div>`
            : nothing}
        </div>

        </div><!-- /trigger-wrapper -->

        <!-- Hint text -->
        <div part="hint-text" ?hidden=${!this._hasHintText}>
          <slot name="hint-text" @slotchange=${this._handleHintSlotChange}></slot>
        </div>

        <!-- Default slot: visually hidden from layout without using display:none,
             so light-DOM option content remains crawler-visible as an SEO fallback.
             The hiding rules live in the component stylesheet (not inline) to avoid
             being stripped by Content-Security-Policy style-src on the host page. -->
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-select': AscSelect;
  }
}
